import React, { useState } from "react";
import { Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useContent } from "../context/ContentContext";
import { saveContent, formatApiError, triggerRebuild } from "../lib/api";
import { setByPath, deepClone } from "../lib/paths";
import { SEO_SCHEMA } from "./seoSchema";
import { Field } from "./AdminField";

export const SeoEditor = () => {
  const { content, setContent } = useContent();
  const [draft, setDraft] = useState(() => deepClone(content));
  const [saving, setSaving] = useState(false);

  const update = (path, value) => setDraft((d) => setByPath(d, path, value));

  const save = async () => {
    setSaving(true);
    try {
      const saved = await saveContent(draft);
      setContent(saved);
      triggerRebuild("seo settings edited");
      toast.success("SEO settings saved — site rebuild starting in the background.");
    } catch (err) {
      toast.error(formatApiError(err) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl" data-testid="seo-editor">
      <div className="flex items-start justify-between mb-6 sticky top-0 bg-cream py-3 z-10 gap-4">
        <div>
          <h1 className="font-serif text-3xl text-ink">SEO</h1>
          <p className="text-sm text-ink-muted">How the site appears in Google, Bing, and social link previews.</p>
        </div>
        <button
          data-testid="save-seo-btn"
          onClick={save}
          disabled={saving}
          className="shrink-0 inline-flex items-center gap-2 rounded-full bg-ink text-cream px-6 py-3 text-sm font-semibold hover:bg-sage-dark transition-colors disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
        </button>
      </div>

      <div className="space-y-6">
        {SEO_SCHEMA.map((section) => (
          <section key={section.key} data-testid={`seo-section-${section.key}`} className="rounded-xl border border-cream-2 bg-white p-6">
            <div className="mb-5">
              <h2 className="font-serif text-xl text-ink">{section.label}</h2>
              {section.description && (
                <p className="text-sm text-ink-muted mt-1">{section.description}</p>
              )}
            </div>
            <div className="space-y-5">
              {section.fields.map((field) => (
                <Field key={field.path} field={field} draft={draft} update={update} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};

export default SeoEditor;
